import { useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { ExperienceType, TabType } from "@intelligems/ig-types";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { getUserInterfacesFromExperienceDto } from "@src/api/formatters/from-dto/user-interfaces";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { setUseAppPadding } from "@src/store/slices/appSettings";
import { initAudiences } from "@src/store/slices/audiences";
import { initCartTargetingFromVariations } from "@src/store/slices/cartTargeting";
import { initCheckoutBlocksFromVariations } from "@src/store/slices/checkoutBlocks";
import {
  initCustomMetrics,
  initKeyMetrics,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { resetExperienceProducts } from "@src/store/slices/experienceProducts";
import { initExperienceProductTargeting } from "@src/store/slices/experienceProductTargeting";
import {
  initExperimentFromDto,
  selectedExperienceState,
} from "@src/store/slices/experiences";
import { setCreationModPreset } from "@src/store/slices/experienceInfoState";
import { isSyntheticCategorySwapPending } from "@src/features/experiments/hooks/experience-route-swap-flag";
import { setBypassUnsavedChanges } from "@src/store/slices/global";
import { initOffersFromVariations } from "@src/store/slices/offers";
import { initOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { initComponents } from "@src/store/slices/experienceComponents";
import { initOnsiteInjections } from "@src/store/slices/onsiteInjections";
import { initPageTargeting } from "@src/store/slices/pageTargeting";
import { initRedirectsFromVariationsState } from "@src/store/slices/redirects";
import { initUpsellProductRulesFromVariations } from "@src/store/slices/upsellProductRules";
import { initUserInterfaces } from "@src/store/slices/userInterfaces";
import {
  ensureRateGroupOrder,
  initVariations,
} from "@src/store/slices/variations";

import { useGetExperience } from "../experiences/get-experience";
import { getOnsiteEditsFromExperienceDto } from "../formatters/from-dto/onsite-edits";
import { getComponentsFromExperienceDto } from "@src/api/formatters/from-dto/components";
import { getOnsiteInjectionsFromExperienceDto } from "../formatters/from-dto/onsite-injections";

const typeToDisplayName: Partial<Record<ExperienceType, string>> = {
  "content/onsiteEdits": "Onsite Test",
  "content/url": "URL Redirect Test",
  "content/template": "Template Test",
  "content/theme": "Theme Test",
  "content/advanced": "Split Test",
  "personalization/offer": "Offer Test",
  checkoutBlock: "Checkout Block Test",
  pricing: "Pricing Test",
  shipping: "Shipping Test",
  postPurchase: "Post Purchase Test",
};

const getDefaultTestName = (testFlowType: ExperienceType): string => {
  const date = new Date();
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  const baseName = typeToDisplayName[testFlowType] ?? "Test";
  return `${baseName} · ${month} ${day}`;
};

export const useAndSetGenericExperimentFromUrl = () => {
  const { id } = useParams<{ id: string }>();
  const { data: org } = useGetOrg();
  const integrations = org.org.integrations;
  const [searchParams] = useSearchParams();
  const { state } = useLocation();

  const dispatch = useDispatch();
  const experienceState = useSelector(selectedExperienceState);

  const query = useGetExperience(id);

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );

  useEffect(() => {
    // `useRedirectOnCategorySwitch` mounts this route as a side effect of
    // swapping to the other category's route tree for the SAME in-progress
    // experience — not a real arrival at this URL. This effect is written
    // for a genuine arrival: the `id` branch would refetch and overwrite
    // any unsaved local edits with server state, and the no-`id` (create)
    // branch would reinit a BLANK draft from `searchParams.get("type")`
    // (deliberately absent on the swap's target URL, so this reads `null`)
    // — wiping the category/type/testTypes the AI just set, and everything
    // else the merchant built, right after the swap carried it over.
    if (isSyntheticCategorySwapPending()) {
      return;
    }

    const testFlowType = searchParams.get("type") as ExperienceType;
    dispatch(setUseAppPadding(false));
    dispatch(setBypassUnsavedChanges(false));

    if (id) {
      dispatch(setCreationModPreset(null));
      if (query.isSuccess) {
        dispatch(
          initExperimentFromDto({
            experience: query.data.experience,
            testFlowType,
            defaultCurrency:
              testFlowType === "pricing"
                ? storeCurrencyConfig?.value?.code || "USD"
                : undefined,
            tab:
              state?.tab ||
              (searchParams.get("tab") as TabType) ||
              "testGroups",
          })
        );
        dispatch(
          initVariations({ variations: query.data.experience.variations })
        );
        dispatch(ensureRateGroupOrder());

        dispatch(
          initKeyMetrics({
            keyMetrics: query.data.experience.experienceKeyMetrics,
          })
        );
        dispatch(
          initCustomMetrics({
            customMetrics: query.data.experience.experienceCustomMetrics,
          })
        );

        // Offer Set
        dispatch(
          initOffersFromVariations({
            variations: query.data.experience.variations,
            isInitialize: true,
          })
        );
        dispatch(
          initRedirectsFromVariationsState({
            variations: query.data.experience.variations,
          })
        );

        dispatch(
          initCheckoutBlocksFromVariations({
            variations: query.data.experience.variations,
          })
        );
        dispatch(
          initUpsellProductRulesFromVariations({
            variations: query.data.experience.variations,
          })
        );
        dispatch(
          initCartTargetingFromVariations({
            variations: query.data.experience.variations,
          })
        );

        // Targeting Sets
        dispatch(
          initPageTargeting({
            pageTargeting: query.data.experience.experiencePageTargeting,
          })
        );
        dispatch(
          initExperienceProductTargeting({
            experienceProductTargeting:
              query.data.experience.experienceProductTargeting,
          })
        );
        dispatch(initAudiences({ audience: query.data.experience.audience }));

        // Visual Edits Set
        dispatch(
          initOnsiteInjections(
            getOnsiteInjectionsFromExperienceDto(
              query.data.experience.variations
            )
          )
        );
        dispatch(
          initOnsiteEdits(
            getOnsiteEditsFromExperienceDto(query.data.experience.variations)
          )
        );
        dispatch(
          initComponents(
            getComponentsFromExperienceDto(query.data.experience.variations)
          )
        );

        dispatch(
          initUserInterfaces(
            getUserInterfacesFromExperienceDto(query.data.experience)
          )
        );
      }
    } else {
      // Read back per test group, long after this arrival.
      dispatch(setCreationModPreset(state?.preset ?? null));
      dispatch(
        initExperimentFromDto({
          experience: undefined,
          integrations: integrations,
          testFlowType,
          defaultCurrency:
            testFlowType === "pricing"
              ? storeCurrencyConfig?.value?.code || "USD"
              : undefined,
          name: state?.name ?? getDefaultTestName(testFlowType),
          description: state?.description,
          tab: state?.tab,
        })
      );
      dispatch(initVariations({ variations: undefined }));

      dispatch(resetExperienceProducts());
      dispatch(
        initAudiences({
          audience: undefined,
          priceTestDefaultCurrency:
            testFlowType === "pricing" || testFlowType === "postPurchase"
              ? storeCurrencyConfig?.value?.code || "USD"
              : undefined,
        })
      );
      dispatch(initOnsiteEdits([]));
      dispatch(initComponents([]));
      dispatch(
        initKeyMetrics({
          keyMetrics: [],
          shouldShowProfit: shouldShowProfit?.value,
          hasTestPostPurchase: testFlowType === "postPurchase",
        })
      );
    }
  }, [id, query.isSuccess]);

  return { experienceState, query: query?.data?.experience };
};
